import { Link } from 'react-router';
import { Modal, ModalContent, ModalBody, Accordion, AccordionItem, Chip } from '@nextui-org/react';
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowDown } from 'react-icons/md';

import tivyApplause from '/images/tivy_applause.svg';
import { Button } from '@/components/Button';
import { DonorScenario } from '@/types/scenarios';
import {
  getChipColorsByScore,
  getScoreLabel
} from '@/pages/donor-simulation/detailed-feedback/components/skill-section';
import DonorSentimentSection from '@/pages/donor-simulation/detailed-feedback/components/donor-sentiment-section';
import { useResponsive } from '@/hooks/useResponsive';
import { SKILLS, DONOR_SENTIMENT } from '../mocks';

export const FeedbackModal = ({ scenario, onOpenChange }: { scenario: DonorScenario; onOpenChange: () => void }) => {
  return (
    <ModalWrapper onOpenChange={onOpenChange}>
      <div className="flex flex-row items-center gap-4">
        <img src={tivyApplause} alt="Well done" className="h-16" />
        <div className="text-left space-y-1">
          <h2 className="text-base md:text-xl">
            <strong>Great job!</strong> You completed the simulation <strong>{scenario.title}.</strong>
          </h2>
          <p className="text-sm md:text-lg text-foreground-600">
            Review your performance and get personalized coaching insights below.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start gap-6">
        <div className="flex-1">
          <Accordion variant="splitted" itemClasses={{ title: 'font-semibold text-base' }}>
            {SKILLS.map((skill, index) => (
              <AccordionItem
                key={index}
                aria-label={skill.name}
                title={skill.name}
                subtitle={skill.summary}
                indicator={({ isOpen }) => {
                  const Icon = isOpen ? MdOutlineKeyboardArrowRight : MdOutlineKeyboardArrowDown;
                  return <Icon size={26} className="text-black p-1 rounded-md bg-lightPurple-50" />;
                }}
              >
                <div className="flex flex-col divide-y divide-neutral-100">
                  {skill.indicators.map((item, idx) => {
                    return (
                      <div
                        key={idx}
                        className="flex justify-between items-center p-2 rounded-md hover:bg-lightPurple-50/40"
                      >
                        <p className="text-sm font-normal">{item.name}</p>
                        <div className="flex gap-2 items-center">
                          <Chip className={getChipColorsByScore(item.score)}>{getScoreLabel(item.score)}</Chip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="w-full md:w-[400px] [&>div]:bg-white">
          <DonorSentimentSection donorSentiment={DONOR_SENTIMENT} />
        </div>
      </div>

      <div className="bg-gradient-to-r from-orange-200 to-pink-200 mt-6 flex flex-col md:flex-row items-center justify-between gap-4 p-5 rounded-lg">
        <div className="text-center md:text-left space-y-2">
          <h3 className="text-xl font-bold">Coach Tivy is here for you!</h3>
          <p className="text-sm md:text-base leading-6">
            Create an account and talk to Coach Tivy to get expert fundraising guidance in real-time.
          </p>
        </div>
        <Button label="Talk to Coach Tivy" component={Link} to="/register" className="hover:bg-orange !text-white" />
      </div>
    </ModalWrapper>
  );
};

const ModalWrapper = ({ children, onOpenChange }: { children: React.ReactNode; onOpenChange: () => void }) => {
  const { isMobile } = useResponsive();

  return (
    <Modal
      isOpen
      backdrop="opaque"
      onOpenChange={onOpenChange}
      className="lg:max-w-[80vw] mt-0"
      size={isMobile ? 'full' : '4xl'}
      placement={isMobile ? 'top' : 'center'}
      scrollBehavior={isMobile ? 'outside' : 'normal'}
    >
      <ModalContent>
        <ModalBody className="bg-purple-50 p-6 md:p-10 text-center space-y-6">{children}</ModalBody>
      </ModalContent>
    </Modal>
  );
};
